import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Menu, Slider } from '@fragments';
import {
  IconArrowBarToDown, IconArrowBarToUp, IconArrowDown, IconArrowUp, IconCopy, IconDots, IconDroplet, IconLayoutAlignBottom, IconLayoutAlignCenter,
  IconLayoutAlignLeft, IconLayoutAlignMiddle, IconLayoutAlignRight, IconLayoutAlignTop, IconLock, IconLockOpen, IconShadow, IconSparkles, IconTrash,
} from '@tabler/icons-react';
import { Divider, PopoverAction } from '../controls';
import EffectsPanel from './EffectsPanel';

const ARRANGE = [
  { where: 'front', icon: IconArrowBarToUp }, { where: 'forward', icon: IconArrowUp },
  { where: 'backward', icon: IconArrowDown }, { where: 'back', icon: IconArrowBarToDown },
];
// The whole list at once, as wide and as tall as its items (Popper `fitContent`, `fitHeight`).
const MENU_CONFIG = { fitContent: true, fitHeight: true };
const ALIGN = [
  { edge: 'left', icon: IconLayoutAlignLeft }, { edge: 'center', icon: IconLayoutAlignCenter }, { edge: 'right', icon: IconLayoutAlignRight },
  { edge: 'top', icon: IconLayoutAlignTop }, { edge: 'middle', icon: IconLayoutAlignMiddle }, { edge: 'bottom', icon: IconLayoutAlignBottom },
];

// The layer's "more" actions (duplicate, lock, order, alignment to the page, delete): the
// inspector's ⋯ menu and the right-click menu (ContextMenu) list the same ones.
export function layerMenuItems(layer, editor, t) {
  const locked = layer.locked;
  return [
    { label: t('duplicate'), icon: <IconCopy />, shortcut: 'Ctrl+D', onClick: () => editor.duplicate(layer.id) },
    { label: t(locked ? 'unlockLayer' : 'lockLayer'), icon: locked ? <IconLockOpen /> : <IconLock />, onClick: () => editor.update(layer.id, { locked: !locked }, { key: null }) },
    { divider: true },
    ...ARRANGE.map(item => ({ label: t(`arrange_${item.where}`), icon: <item.icon />, disabled: locked, onClick: () => editor.arrange(layer.id, item.where) })),
    { divider: true },
    ...ALIGN.map(item => ({ label: `${t('alignToPage')}: ${t(`align_${item.edge}`).toLowerCase()}`, icon: <item.icon />, disabled: locked, onClick: () => editor.align(layer.id, item.edge) })),
    { divider: true },
    { label: t('delete'), icon: <IconTrash />, variant: 'error', shortcut: 'Del', onClick: () => editor.remove([layer.id]) },
  ];
}

// What every selected layer offers: its effects (shadows, glass, blur, texture), opacity,
// then one menu for the rest and, when the host has a chat, a scoped AI edit.
export default function LayerActions({ layer, editor, onAskAI, colors }) {
  const { t } = useTranslation('artEditor');
  const items = layerMenuItems(layer, editor, t);
  return <>
    <Divider />
    <PopoverAction icon={IconShadow} label={t('effects')}>
      <EffectsPanel layer={layer} colors={colors} update={(patch, meta) => editor.update(layer.id, patch, meta)} />
    </PopoverAction>
    <PopoverAction icon={IconDroplet} label={t('opacity')}>
      <Slider label={t('opacity')} min={0} max={100} value={Math.round(layer.opacity * 100)} formatLabel={v => `${v}%`}
        onChange={(_, value) => editor.update(layer.id, { opacity: value / 100 }, { key: `${layer.id}:opacity` })} />
    </PopoverAction>
    <Menu items={items} placement="top-end" config={MENU_CONFIG}>
      <Button size="small" variant="text" color="overlay" preventLoading icon={<IconDots />} aria-label={t('moreActions')} tooltip={t('moreActions')} />
    </Menu>
    {onAskAI && <>
      <Divider />
      <Button className="art-inspector__ai" size="small" variant="rounded" color="ai" preventLoading startIcon={<IconSparkles />} aria-label={t('askAI')} onClick={() => onAskAI([layer])}>
        <span className="art-inspector__ai-label">{t('askAI')}</span>
      </Button>
    </>}
  </>;
}
